import React, { useState } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { Fragment } from 'react';
import {
  XMarkIcon,
  ShieldCheckIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { getSession } from '../../api/supabase';

const MFADisableSupportForm = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { profile } = useUserProfile();
  
  const [formData, setFormData] = useState({
    requestType: 'bug',
    title: 'Request to Disable Two-Factor Authentication',
    description: `I need assistance disabling two-factor authentication on my account.

Reason for disabling MFA:
[ ] Lost access to my authenticator app
[ ] Switched to a new phone/device
[ ] No longer need MFA protection
[ ] Other: _______________

I understand that disabling MFA will reduce the security of my account. I would like the Sparvi support team to manually remove MFA from my account.

Please confirm my identity before making this change.`,
    priority: 'high'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description) {
      setSubmitStatus({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Get the current session to obtain the auth token
      const session = await getSession();
      const token = session?.access_token;
      
      if (!token) {
        setSubmitStatus({ 
          type: 'error', 
          message: 'Authentication token not found. Please refresh the page and try again.' 
        });
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/support/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          requestType: formData.requestType,
          title: formData.title,
          description: formData.description,
          priority: formData.priority,
          userInfo: {
            name: `${user?.user_metadata?.first_name || ''} ${user?.user_metadata?.last_name || ''}`.trim(),
            email: user?.email,
            organization: profile?.organization?.name || user?.user_metadata?.organization_name,
            userId: user?.id
          }
        })
      });

      if (response.ok) {
        setSubmitStatus({ 
          type: 'success', 
          message: 'Your MFA disable request has been sent successfully! Our support team will review your request and contact you within 24 hours.' 
        });
        
        // Close form after successful submission
        setTimeout(() => {
          setSubmitStatus(null);
          onClose();
        }, 3000);
      } else {
        const errorData = await response.json();
        setSubmitStatus({ 
          type: 'error', 
          message: errorData.message || 'Failed to send support request. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Error submitting MFA disable request:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSubmitStatus(null);
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between">
                  <div className="flex items-center">
                    <ShieldCheckIcon className="h-6 w-6 text-orange-500 mr-2" />
                    <span>Request MFA Removal</span>
                  </div>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </DialogTitle>

                <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ShieldCheckIcon className="h-5 w-5 text-orange-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-orange-800">Manual Assistance Required</h3>
                      <div className="mt-2 text-sm text-orange-700">
                        <p>Due to security limitations, disabling two-factor authentication requires manual verification by our support team. Please review and submit the form below.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="mt-6">
                  {/* Title Field */}
                  <div className="mb-6">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                      Request Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      id="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  {/* Priority Selection */}
                  <div className="mb-6">
                    <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
                      Priority
                    </label>
                    <select
                      name="priority"
                      id="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      disabled={isSubmitting}
                    >
                      <option value="medium">Medium - Standard request</option>
                      <option value="high">High - Need access soon</option>
                      <option value="critical">Critical - Locked out of account</option>
                    </select>
                  </div>

                  {/* Description Field */}
                  <div className="mb-6">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Details <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      rows={8}
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Please provide details about why you need MFA disabled..."
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      disabled={isSubmitting}
                      required
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      Please select your reason and provide any additional context. Our team will verify your identity before making changes.
                    </p>
                  </div>

                  {/* User Information Display */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-md">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Your Information</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Name:</strong> {`${user?.user_metadata?.first_name || ''} ${user?.user_metadata?.last_name || ''}`.trim() || 'Not provided'}</p>
                      <p><strong>Email:</strong> {user?.email}</p>
                      <p><strong>Organization:</strong> {profile?.organization?.name || user?.user_metadata?.organization_name || 'Not provided'}</p>
                    </div>
                  </div>

                  {/* Status Message */}
                  {submitStatus && (
                    <div className={`mb-4 p-4 rounded-md ${
                      submitStatus.type === 'success' 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <p className={`text-sm ${
                        submitStatus.type === 'success' ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {submitStatus.message}
                      </p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                      onClick={handleClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || !formData.title || !formData.description}
                      className="inline-flex items-center rounded-md border border-transparent bg-orange-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending Request...
                        </>
                      ) : (
                        <>
                          <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                          Send Request
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MFADisableSupportForm;